import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { DateRange } from "react-day-picker";
import { format } from "date-fns";

interface DatePickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: DateRange | undefined;
  onChange: (range: DateRange | undefined) => void;
  onApply: () => void;
}

export const DatePickerModal = ({ open, onOpenChange, value, onChange, onApply }: DatePickerModalProps) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onOpenChange(false);
      }
    };
    
    const handleArrows = (e: KeyboardEvent) => {
      if (!open) return;
      
      if (e.key === 'Enter') {
        if (!value?.from || !value?.to || value.to <= value.from) return;
        onApply();
        onOpenChange(false);
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    window.addEventListener('keydown', handleArrows);
    return () => {
      window.removeEventListener('keydown', handleEscape);
      window.removeEventListener('keydown', handleArrows);
    };
  }, [open, onOpenChange, value, onApply]);

  const handleClear = () => {
    onChange(undefined);
  };

  const handleApplyClick = () => {
    onApply();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent 
          className="bg-charcoal border border-gold/20 text-ivory max-w-[95vw] sm:max-w-fit p-0 z-[1001] max-h-[90vh] overflow-y-auto"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="p-4 sm:p-6"
            >
              <div className="mb-4 sm:mb-6">
                <h3 className="text-xl sm:text-2xl font-bold text-ivory mb-3 sm:mb-4">Select dates</h3>
                {value?.from && (
                  <div className="bg-gradient-to-r from-gold/20 via-gold/10 to-gold/20 rounded-2xl p-3 sm:p-5 border-2 border-gold/30 backdrop-blur-sm">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0">
                      {/* Check-in */}
                      <div className="flex-1 w-full sm:w-auto">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-2 h-2 rounded-full bg-gold shadow-[0_0_8px_rgba(212,175,55,0.8)]" />
                          <span className="text-xs text-ivory/70 uppercase tracking-wider font-semibold">Check-in</span>
                        </div>
                        <p className="text-lg sm:text-xl font-bold text-gold">
                          {format(value.from, "MMM dd, yyyy")}
                        </p>
                        <p className="text-xs text-ivory/60 mt-1">
                          {format(value.from, "EEEE")}
                        </p>
                      </div>

                      {/* Arrow & Nights */}
                      {value.to && (
                        <>
                          <div className="flex sm:flex-col items-center justify-between sm:justify-center w-full sm:w-auto sm:mx-4 my-2 sm:my-0">
                            <div className="text-gold/40 text-2xl hidden sm:block">→</div>
                            <div className="px-4 py-2 bg-gold/30 rounded-xl border border-gold/50 backdrop-blur-sm sm:mt-2">
                              <span className="text-xs text-ivory/70 uppercase tracking-wider block text-center font-semibold">Nights</span>
                              <p className="text-xl sm:text-2xl font-bold text-gold text-center mt-1">
                                {Math.ceil((value.to.getTime() - value.from.getTime()) / (1000 * 60 * 60 * 24))}
                              </p>
                            </div>
                          </div>

                          {/* Check-out */}
                          <div className="flex-1 w-full sm:w-auto sm:text-right">
                            <div className="flex items-center gap-2 mb-1 sm:justify-end">
                              <span className="text-xs text-ivory/70 uppercase tracking-wider font-semibold">Check-out</span>
                              <div className="w-2 h-2 rounded-full bg-gold shadow-[0_0_8px_rgba(212,175,55,0.8)]" />
                            </div>
                            <p className="text-lg sm:text-xl font-bold text-gold">
                              {format(value.to, "MMM dd, yyyy")}
                            </p>
                            <p className="text-xs text-ivory/60 mt-1">
                              {format(value.to, "EEEE")}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="w-full overflow-x-auto">
                <Calendar
                  mode="range"
                  selected={value}
                  onSelect={onChange}
                  numberOfMonths={window.innerWidth >= 768 ? 2 : 1}
                  showOutsideDays={false}
                  disabled={(date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return date < today;
                  }}
                  initialFocus
                  className="pointer-events-auto mx-auto"
                />
              </div>
              
              <div className="flex gap-3 sm:gap-4 mt-4 sm:mt-6">
                <Button
                  variant="outline"
                  onClick={handleClear}
                  className="flex-1 h-11 sm:h-12 border-2 border-ivory/20 hover:border-gold/40 text-ivory hover:text-gold font-semibold text-sm sm:text-base transition-all"
                >
                  Clear
                </Button>
                <Button
                  onClick={handleApplyClick}
                  disabled={!value?.from || !value?.to || value.to <= value.from}
                  className="flex-1 h-11 sm:h-12 bg-gradient-to-r from-gold via-gold-glow to-gold hover:shadow-[0_0_25px_rgba(212,175,55,0.6)] text-charcoal font-bold text-sm sm:text-base disabled:opacity-40 disabled:cursor-not-allowed transition-all disabled:hover:shadow-none"
                >
                  Apply Dates
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};
